import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { Switch } from "./ui/switch";
import { Contrast } from "lucide-react";
import { track } from "../lib/usageTracker";

const TEXT_SIZES = [
  { key: "compact", label: "Aa", px: "15px", desc: "Compact" },
  { key: "default", label: "Aa", px: "17px", desc: "Default" },
  { key: "large", label: "Aa", px: "19px", desc: "Large" },
];

function applyTextSize(key) {
  const opt = TEXT_SIZES.find((t) => t.key === key) || TEXT_SIZES[1];
  document.documentElement.style.setProperty("--nw-base-font", opt.px);
}

function applyHighContrast(on) {
  document.documentElement.classList.toggle("hc-theme", !!on);
}

/**
 * PR #887 review, R1 — the trigger's OWN contrast glyph, drawn distinctly
 * from lucide's `Contrast` icon (still used, unchanged, inside the popover
 * row below — that one is decorative content, never drawn by an artboard).
 * `SetUp.dc.html:63`/`CoachSetUp.dc.html:55`/`EmptyRecord.dc.html`/
 * `OneInnings.dc.html`/`CoachPortalEmpty.dc.html` all draw the SAME shape
 * at this spot: `<circle r="9"/>` plus `<path d="M12 3a9 9 0 0 0 0 18z"
 * fill="{colour}" stroke="none"/>` — a ring with its LEFT half filled, not
 * lucide's `Contrast` (a filled circle split by a vertical bar, a visibly
 * different glyph on close inspection). `currentColor` on both the stroke
 * and the fill, so this one glyph reads correctly on either surface: white
 * (navy bars, via the existing CSS override that already forces `color`)
 * or `#64748B` (white bars, this component's own default below) — no
 * separate light/dark variant needed.
 */
function ContrastGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * `hideTextSize` — issue #797, Andreas's decision 2026-09-23: retire the
 * text-size control on a rebuilt screen (browser zoom is the accessibility
 * path there instead; the kit's fixed-px type ramp, #792/#826, already
 * means the control moves nothing on one). Passed down by `Layout.jsx`,
 * computed from `hidesTextSizeControl(location.pathname)`
 * (`lib/chromelessRoutes.js`) — see that function's own docstring for
 * exactly which routes count as "rebuilt".
 *
 * HIGH CONTRAST IS NOT PART OF THIS DECISION. The issue is scoped to the
 * text-size control by name throughout (title, body, both linked comments);
 * high contrast is a different accessibility feature that keeps working on
 * every screen, rebuilt or not. So `hideTextSize` removes only the "Text
 * size" section (the label and the three Compact/Default/Large buttons)
 * from the popover — never the popover itself, and never the high-contrast
 * switch beneath it.
 *
 * `compact` — issue #940 (PR #957 review round 4, decoupled from
 * `hideTextSize` on an orchestrator ruling: D25 keeps text size on
 * `/development`, while the drawing and the D160/#930 navy-bar visuals
 * still apply there). Gives ONLY the trigger's own visuals — the 44px (#1118; 32 before) D160
 * tap area, the #930/#955 inset focus ring, and the white contrast glyph
 * (`ContrastGlyph` below) in place of the shipped "Aa" — regardless of
 * whether the popover offers text size. `hideTextSize` alone still decides
 * the POPOVER content (the "Text size" section, and the aria-label/title
 * wording below, which describes what the popover actually contains, not
 * how the trigger looks).
 *
 * `isCompact = compact || hideTextSize` is what the trigger's own visuals
 * actually key on, so EVERY EXISTING CALLER — all eleven pass
 * `hideTextSize` alone, never `compact` — renders BYTE-IDENTICAL to
 * before: `hideTextSize` implying `isCompact` keeps this file the single
 * place that fact lives, rather than touching eleven call sites to add a
 * second prop they would always set to the same value as the first.
 * `DevelopmentChrome.jsx` is the one caller that tells the two apart:
 * `compact` (the navy-bar visuals), no `hideTextSize` (text size stays,
 * D25) — `isCompact` is true, `hideTextSize` is false, and the two
 * conditions below that still read `hideTextSize` directly (the popover's
 * text-size section, the aria-label/title pair) correctly stay off it.
 *
 * THE TRIGGER GLYPH CHANGES TOO ON A COMPACT TRIGGER, deliberately, not
 * left as "Aa": `SetUp.dc.html:63`/`CoachSetUp.dc.html:55`/
 * `EmptyRecord.dc.html`/`OneInnings.dc.html`/`CoachPortalEmpty.dc.html`/
 * `Reflect.dc.html:45` all draw the contrast glyph at this spot, drawn
 * shape, not lucide's `Contrast` (the high-contrast row still uses that
 * one, unchanged). On `DevelopmentChrome.jsx`'s own `compact`-without-
 * `hideTextSize` combination the glyph still shows (matching the drawing),
 * even though the popover it opens offers more than contrast — the
 * trigger's glyph is what the drawing draws, not a promise about the
 * popover's own contents, and #792's "opens to nothing but a contrast
 * toggle" complaint was about the OPPOSITE mismatch (a fuller-looking
 * trigger promising more than an empty popover delivered), which this
 * combination does not reproduce.
 *
 * #780 (D140) — `hideTextSize` used to be exactly "this is a rebuilt
 * screen" (`hidesTextSizeControl(pathname) || retiredByScreen`,
 * `Layout.jsx`'s own computation) for every screen but one; `isCompact`
 * now carries the glyph-swap-and-resize fact this paragraph originally
 * described. `EmptyRecord.dc.html`/`OneInnings.dc.html` (round 5) draw
 * this glyph at 18px, not the 16px it shipped at, and the trigger's own
 * tap area grows from 36px (`w-9 h-9`) to the drawn 44px floor — the same
 * `h-11` `NotificationsBell.jsx` already uses two elements to the left.
 * The "Aa" trigger (`isCompact` false, every screen not yet rebuilt AND
 * not `compact`) is UNTOUCHED — same 36px box, same 13px/9px "Aa" — so
 * this changes nothing for a screen whose flag is off.
 */
/**
 * Issue #892 (D171 X2) — see `NotificationsBell.jsx`'s own
 * `compactFocusRingFor` for the full reasoning (a `ring` clips against this
 * control's own D160 44×barHeight tap area (32 before #1118); a `-2px` `outline` sits inside
 * it instead). Duplicated rather than imported: the two files share no
 * module today, and a three-line focus helper is not worth a new one.
 */
const COMPACT_FOCUS_RING =
  // #931 — `nw-topbar-focus` is a marker class only (no rules of its own
  // here): `index.css`'s `html.hc-theme .nw-topbar-focus:focus-visible`
  // targets exactly these four controls so the hc-theme global rule's
  // outset ring can be pulled inside without touching every other
  // focusable element on the page.
  "focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] nw-topbar-focus";
// #931, D171 X2 ruling (already-decided: "white on navy and navy on
// white" applies under hc-theme too, per-surface exactly like the non-hc
// outline colour two lines below) — `nw-topbar-focus-hc-navy` only on the
// white-surface branch swaps the hc-theme global rule's gold for
// `var(--nw-navy)` (>=3:1 against the white bar; gold measured ~2.38:1
// and was ruled out). The navy-surface branch keeps the global rule's
// gold untouched (already ~7.3:1 against the navy bar).
const compactFocusRingFor = (surface) =>
  surface === "white" ? `${COMPACT_FOCUS_RING} focus-visible:outline-[#0B2E59] nw-topbar-focus-hc-navy` : `${COMPACT_FOCUS_RING} focus-visible:outline-white`;

export function AccessibilityMenu({ hideTextSize = false, compact = false, surface = "navy" }) {
  // See this file's own `compact` doc comment above — the trigger's
  // visuals key on EITHER prop; the popover's text-size section and the
  // aria-label/title pair key on `hideTextSize` alone.
  const isCompact = compact || hideTextSize;
  const [textSize, setTextSize] = useState(() => localStorage.getItem("nw_text_size") || "default");
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem("nw_high_contrast") === "1");

  // Keep in sync when Settings page changes them
  useEffect(() => {
    const sync = () => {
      setTextSize(localStorage.getItem("nw_text_size") || "default");
      setHighContrast(localStorage.getItem("nw_high_contrast") === "1");
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const chooseTextSize = (key) => {
    setTextSize(key);
    localStorage.setItem("nw_text_size", key);
    applyTextSize(key);
  };

  const toggleHighContrast = (on) => {
    setHighContrast(on);
    localStorage.setItem("nw_high_contrast", on ? "1" : "0");
    applyHighContrast(on);
  };

  return (
    <Popover onOpenChange={(open) => { if (open) track("menu.open", { item: "accessibility" }); }}>
      <PopoverTrigger asChild>
        {/* Fix round (review, probe 2 nit): "High contrast" reads as the
            name of an on/off switch, which this trigger isn't — it opens a
            popover. "Contrast options" describes the popover's actual
            contents (today, just the one switch) without implying a
            toggle. */}
        <button
          type="button"
          data-testid="topbar-accessibility"
          aria-label={hideTextSize ? "Contrast options" : "Accessibility options"}
          title={hideTextSize ? "Contrast options" : "Text size & contrast"}
          className={
            isCompact
              ? // `rounded-full` REMOVED (PR #887 review, B3 follow-up) — on a
                // 44×barHeight rectangle (32 before #1118), `rounded-full`'s huge radius
                // (min(width,height)/2 = 16px) rounds the corners enough
                // that `elementFromPoint` genuinely excludes them: probing
                // this button's own top-left corner hit the WRAPPING div
                // instead (confirmed with a real `page.mouse.click`, not
                // just the geometry check — the click never opened the
                // popover). D160 asks for a plain rectangular tap area
                // tiling edge to edge with no overlap; a rounded one
                // breaks that at exactly the corners a probe would find.
                // Issue #892 (D171 X2) — `compactFocusRingFor(surface)`
                // above replaces the shared `ring-ring`; see that helper.
                `text-[#64748B] flex items-center justify-center ${compactFocusRingFor(surface)} transition`
              : // #1118 (D221 G4) — a 44px hit area around the drawn 36px
                // circle (`h-11 w-11` button, `-m-1` = (2.75rem - 2.25rem)/2
                // so the layout footprint is unchanged; the circle itself is
                // the inner span below). Same idea as the bell's
                // `h-11 w-11 sm:h-9 sm:w-9`, but this one keeps the circle at
                // every width.
                "group h-11 w-11 -m-1 rounded-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          }
          style={isCompact ? { width: "var(--nw-size-tap)" } : undefined}
        >
          {isCompact ? (
            /* PR #887 review, B1/R1 — borderless, no background: the ring
               and `bg-white` were the shipped white-variant style, wrong
               for both drawn surfaces (`SetUp.dc.html:63` draws a bare
               glyph, no box at all).
               PR #887 review, B3 (D160) — `width: var(--nw-size-tap)` (32 before #1118), no explicit
               height: `EndControlsRow` (kit/TopBar.jsx) gives this
               button's own parent the bar's full height, and flexbox's
               default `align-items: stretch` fills it to match, so this
               control's tap area is exactly 44px wide × the full bar
               height, tiled edge to edge with its neighbours — no
               overlap. Replaces the earlier `-m-[13px]` 44px box, which
               DID overlap its neighbours (found live: a click just right
               of this glyph opened the account menu instead). */
            <ContrastGlyph size={18} />
          ) : (
            <span
              className="w-9 h-9 rounded-full border-2 border-slate-200 bg-white text-slate-700 group-hover:border-[#0B2E59] group-hover:text-[#0B2E59] flex items-center justify-center transition"
            >
              <span className="font-display font-bold text-[13px] leading-none">
                <span style={{ fontSize: "13px" }}>A</span>
                <span style={{ fontSize: "9px" }}>a</span>
              </span>
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4" data-testid="accessibility-popover">
        {!hideTextSize && (
          <div data-testid="topbar-text-size">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">Text size</div>
            <div className="grid grid-cols-3 gap-2">
              {TEXT_SIZES.map((t) => {
                const active = textSize === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => chooseTextSize(t.key)}
                    data-testid={`quick-text-size-${t.key}`}
                    className={`rounded-lg border-2 py-2 transition-all flex flex-col items-center ${
                      active
                        ? "border-[#0B2E59] bg-[#0B2E59]/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <span className="font-display font-black text-slate-900" style={{ fontSize: t.px }}>
                      {t.label}
                    </span>
                    <span className="text-[10px] text-slate-500 mt-0.5">{t.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={`flex items-center justify-between gap-3 ${hideTextSize ? "" : "border-t border-slate-100 mt-4 pt-4"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
              <Contrast size={14} color="#0B2E59" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">High contrast</div>
              <div className="text-[11px] text-slate-500 truncate">WCAG-friendly colours</div>
            </div>
          </div>
          {isCompact ? (
            /* #1394 (D221 G4, D354) — the compact (rebuilt-screen) menu's High
               contrast switch is a transparent 44x44 tap box with the same
               36x20 track drawn inside it. The margin pulls the margin box
               back to the track's own h-5 w-9 ((2.25rem - 44px) / 2 across,
               (1.25rem - 44px) / 2 down), so the row does not grow and the
               track stays where it was drawn; the box reaches into the
               popover's own 16px padding. The shipped "Aa" menu
               (`isCompact` false, D217) keeps the bare `Switch` below. */
            <SwitchPrimitives.Root
              checked={highContrast}
              onCheckedChange={toggleHighContrast}
              data-testid="quick-high-contrast"
              className="group peer inline-flex h-[var(--nw-size-tap)] w-[var(--nw-size-tap)] shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none focus-visible:outline-none my-[calc((1.25rem_-_var(--nw-size-tap))/2)] mx-[calc((2.25rem_-_var(--nw-size-tap))/2)]"
            >
              <span
                data-switch-track=""
                className="inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent shadow-sm transition-colors group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background group-data-[state=checked]:bg-primary group-data-[state=unchecked]:bg-input"
              >
                <SwitchPrimitives.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
              </span>
            </SwitchPrimitives.Root>
          ) : (
            <Switch
              checked={highContrast}
              onCheckedChange={toggleHighContrast}
              data-testid="quick-high-contrast"
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
